/// Menu
//import Metismenu from "metismenujs";
import React, { useContext, useEffect, useReducer, useState, useRef } from "react";
/// Scroll
import PerfectScrollbar from "react-perfect-scrollbar";
import Collapse from 'react-bootstrap/Collapse';
/// Link
import { Link, NavLink, useLocation } from "react-router-dom";
import { useScrollPosition } from "@n8tb1t/use-scroll-position";
import { ThemeContext } from "../../../context/ThemeContext";
import { useSidebar } from "./SidebarContext";

import { MenuList } from './Menu';
import { hasFeatureAccess } from '../../../utils/featureLock';
import { hasDocumentSubscription } from '../../../utils/documentAccess';
import { accessControlService } from '../../../services/accessControlService';
import { submenuAllowedByMyAccess } from '../../../utils/routeAccessConfig';
import {
  hasAdminRole,
  isCorporateAdmin,
  canAccessBranches,
  canAccessStaff,
  canAccessVehicles,
  canAccessDesignations,
  canAccessUsers,
  canAccessWorksheet,
  canAccessDepartments,
  canAccessSubDepartments,
  canAccessUserSettings,
  getCurrentUserRole
} from '../../../utils/roleAccess';

// Helper to normalize role (handles spaces vs underscores)
const normalizeRole = (role) => {
  if (!role) return '';
  return role.toLowerCase().replace(/\s+/g, '_').trim();
};

/** Resolve corporate vs personal Credentials Vault nav for sidebar highlighting (list/detail/add/edit). */
function getCredentialVaultNavSideFromPath(pathname, search = '', state = null) {
  if (!pathname) return null;
  const trimmed =
    pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname;
  const parts = trimmed.split('/').filter(Boolean);
  if (parts[0] !== 'credential-vault') return null;
  const seg = parts[1];
  if (!seg || seg === 'vaultSettings') return null;

  if (seg === 'credentialVaultList') return 'corporate';
  if (seg === 'personalVaultList') return 'personal';

  const sideFromState = state && state.credentialVaultSide;
  if (sideFromState === 'personal' || sideFromState === 'corporate') return sideFromState;

  if (seg === 'addCredential') {
    const sideFromState = state && state.credentialVaultSide;
    if (sideFromState === 'personal' || sideFromState === 'corporate') return sideFromState;
    try {
      const q = search.startsWith('?') ? search.slice(1) : search || '';
      const qs = new URLSearchParams(q);
      if (qs.get('scope') === 'personal') return 'personal';
    } catch (_) {
      /* ignore */
    }
    return 'corporate';
  }

  return null;
}



const reducer = (previousState, updatedState) => ({
  ...previousState,
  ...updatedState,
});

const initialState = {
  active: "",
  activeSubmenu: "",
}


const SideBar = () => {
  const {
    iconHover,
    sidebarposition,
    headerposition,
    sidebarLayout,
  } = useContext(ThemeContext);

  const { isOpen, closeSidebar } = useSidebar();
  const location = useLocation();
  const subscriptionFeatureRequiredPath = '/subscription-feature-required';
  const [state, setState] = useReducer(reducer, initialState);
  const [hasProjectAccess, setHasProjectAccess] = useState(false);
  const [hasBranchAccess, setHasBranchAccess] = useState(false);
  const [hasDepartmentAccess, setHasDepartmentAccess] = useState(false);
  const [hasWorksheetAccess, setHasWorksheetAccess] = useState(false);
  const [hasDocumentAccess, setHasDocumentAccess] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [userManualClick, setUserManualClick] = useState(false);
  const [userPlanFeaturesState, setUserPlanFeaturesState] = useState([]);
  const [menuAccessKeys, setMenuAccessKeys] = useState(null);
  const [menuAccessRoutePaths, setMenuAccessRoutePaths] = useState(null);
  /** After first `/my-access` attempt: if keys stay null (error), deny items with menu_key instead of leaking them via role fallback. */
  const [menuAccessFetchResolved, setMenuAccessFetchResolved] = useState(false);

  // Get user features from localStorage and keep in state so UI reacts to changes
  const loadUserPlanFeatures = () => {
    try {
      const featuresStr = localStorage.getItem('userPlanFeatures');
      if (featuresStr) {
        const parsed = JSON.parse(featuresStr);
        // Handle different possible formats:
        // 1. Array of objects with 'feature' property: [{feature: "project"}, ...]
        // 2. Array of feature keys: ["project", "worksheet", ...]
        // 3. Array of feature objects with 'feature_key': [{feature_key: "project"}, ...]
        let features = [];
        if (Array.isArray(parsed)) {
          features = parsed.map(f => {
            if (typeof f === 'string') return f;
            if (f.feature) return f.feature;
            if (f.feature_key) return f.feature_key;
            if (f.key) return f.key;
            return null;
          }).filter(f => f !== null);
        }
        setUserPlanFeaturesState(features);
      } else {
        setUserPlanFeaturesState([]);
      }
    } catch (e) {
      console.error('Error parsing userPlanFeatures from localStorage:', e);
      setUserPlanFeaturesState([]);
    }
  };

  // Load user role on mount
  useEffect(() => {
    const role = getCurrentUserRole();
    setUserRole(role);
  }, []);

  // Load menu access from Access Control API (`menu_keys`). Successful responses always use strict allow-list (including `[]`).
  // Until the first fetch completes we keep legacy role fallback; after a failed fetch, items with menu_key stay hidden without an allow-list.
  const fetchMenuAccess = async () => {
    try {
      const res = await accessControlService.getMyAccess();
      const keys = res?.data?.data?.menuKeys;
      if (res?.data?.success) {
        setMenuAccessKeys(Array.isArray(keys) ? keys : []);
        const rp = res?.data?.data?.routePaths;
        setMenuAccessRoutePaths(Array.isArray(rp) ? rp : null);
      } else {
        setMenuAccessKeys(null);
        setMenuAccessRoutePaths(null);
      }
    } catch (err) {
      setMenuAccessKeys(null);
      setMenuAccessRoutePaths(null);
    } finally {
      setMenuAccessFetchResolved(true);
    }
  };

  useEffect(() => {
    fetchMenuAccess();
  }, []);

  useEffect(() => {
    const onAccessUpdated = () => fetchMenuAccess();
    window.addEventListener('menu-access-updated', onAccessUpdated);
    return () => window.removeEventListener('menu-access-updated', onAccessUpdated);
  }, []);

  // Load features on mount
  useEffect(() => {
    loadUserPlanFeatures();
  }, [])

  // Mobile detection and resize handler
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Apply menu-toggle class to main wrapper for compatibility
  useEffect(() => {
    const mainWrapper = document.querySelector("#main-wrapper");
    if (mainWrapper) {
      if (isOpen) {
        mainWrapper.classList.add("menu-toggle");
      } else {
        mainWrapper.classList.remove("menu-toggle");
      }
    }
  }, [isOpen]);

  // For scroll
  const [hideOnScroll, setHideOnScroll] = useState(true)
  useScrollPosition(
    ({ prevPos, currPos }) => {
      const isShow = currPos.y > prevPos.y
      if (isShow !== hideOnScroll) setHideOnScroll(isShow)
    },
    [hideOnScroll]
  )

  /// Path - use full pathname for proper matching
  const currentPath = location.pathname;

  // Helper function to normalize path for comparison (handles with/without leading slash)
  const normalizePathForComparison = (path) => {
    if (!path) return '';
    const trimmed = path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path;
    return trimmed.startsWith('/') ? trimmed : '/' + trimmed;
  };

  // Helper function to extract base path prefix (e.g., /task, /settings, /staff)
  const getBasePathPrefix = (path) => {
    if (!path) return null;
    const normalized = normalizePathForComparison(path);
    const parts = normalized.split('/').filter(p => p);
    if (parts.length >= 1) {
      return '/' + parts[0]; // e.g., /task, /settings, /staff, /vehicle, /documents
    }
    return normalized;
  };

  // Helper function to find the most relevant sub-item for a given path
  const findBestSubItemMatch = (normalizedPath, menuItem) => {
    const currentPath = normalizePathForComparison(normalizedPath);
    let bestMatch = null;
    let bestMatchScore = 0;
    
    const currentParts = currentPath.split('/').filter(p => p);
    
    for (const subItem of menuItem.content) {
      if (!subItem.to) continue;
      
      const subItemPath = normalizePathForComparison(subItem.to);
      const subItemParts = subItemPath.split('/').filter(p => p);
      
      // Special handling for Staff: any path starting with /staff/ should match "Staff" submenu
      if (subItem.title === 'Staff' && currentParts.length >= 1 && currentParts[0] === 'staff') {
        const score = 1000; // High score to ensure it's selected
        if (score > bestMatchScore) {
          bestMatch = subItem.title;
          bestMatchScore = score;
        }
      }
      
      // Special handling for Vehicle: any path starting with /vehicle/ should match "Vehicle" submenu
      if (subItem.title === 'Vehicle' && currentParts.length >= 1 && currentParts[0] === 'vehicle') {
        const score = 1000; // High score to ensure it's selected
        if (score > bestMatchScore) {
          bestMatch = subItem.title;
          bestMatchScore = score;
        }
      }

      // Settings → Credentials Vault (vault settings route lives under /credential-vault/)
      if (subItem.title === 'Credentials Vault' && currentParts[0] === 'credential-vault' && currentParts[1] === 'vaultSettings') {
        const score = 1100;
        if (score > bestMatchScore) {
          bestMatch = subItem.title;
          bestMatchScore = score;
        }
      }
      if (
        subItem.menu_key === 'credential_vault_list' &&
        currentParts.length >= 1 &&
        currentParts[0] === 'credential-vault' &&
        currentParts[1] !== 'vaultSettings'
      ) {
        const navSide = getCredentialVaultNavSideFromPath(
          normalizedPath,
          location.search,
          location.state
        );
        const listSeg = subItemParts[1];
        const matches =
          (navSide === 'corporate' && listSeg === 'credentialVaultList') ||
          (navSide === 'personal' && listSeg === 'personalVaultList');
        if (matches) {
          const score = 1000;
          if (score > bestMatchScore) {
            bestMatch = subItem.title;
            bestMatchScore = score;
          }
        }
      }

      // Performance dashboard — always under Tasks menu
      if (subItem.title === 'Performance Report' && currentParts.length >= 2 && currentParts[0] === 'task' && currentParts[1] === 'performance') {
        const score = 1000;
        if (score > bestMatchScore) {
          bestMatch = subItem.title;
          bestMatchScore = score;
        }
      }

      // Special handling for Project: /task/projectdetails, /task/editproject, addproject etc. should match "Project" submenu
      if (subItem.title === 'Project ' && currentParts[0] === 'task' && currentParts[1]?.toLowerCase().startsWith('project')) {
        const score = 1000; // High score to ensure it's selected
        if (score > bestMatchScore) {
          bestMatch = subItem.title;
          bestMatchScore = score;
        }
      }

      // Discussion Forum: detail/edit routes highlight under Forums or Start Conversation
      if (currentParts[0] === 'forum') {
        if (subItem.menu_key === 'forum_list' && (currentParts[1] === 'list' || currentParts[1] === 'detail')) {
          const score = 1000;
          if (score > bestMatchScore) {
            bestMatch = subItem.title;
            bestMatchScore = score;
          }
        }
        if (subItem.menu_key === 'forum_create' && (currentParts[1] === 'create' || currentParts[1] === 'edit')) {
          const score = 1000;
          if (score > bestMatchScore) {
            bestMatch = subItem.title;
            bestMatchScore = score;
          }
        }
      }
      
      // Check if current path is an exact match or starts with sub-item path
      if (currentPath === subItemPath || currentPath.startsWith(subItemPath + '/')) {
        // Longer matches are better (more specific)
        if (subItemPath.length > bestMatchScore) {
          bestMatch = subItem.title;
          bestMatchScore = subItemPath.length;
        }
      }
      
      // Special handling for document routes - map upload, details, folder to "My Documents"
      if (currentParts[0] === 'documents') {
        const documentAction = currentParts[1]?.toLowerCase() || '';
        // Map document management actions to "My Documents"
        if (['upload', 'details', 'folder'].includes(documentAction) && subItem.title === 'My Documents') {
          const score = subItemPath.length;
          if (score > bestMatchScore) {
            bestMatch = subItem.title;
            bestMatchScore = score;
          }
        }
      }
      
      // Check for semantic matches (e.g., settings/adduser matches settings/userlist)
      // If they share the same parent path (e.g., both start with "settings")
      if (currentParts.length >= 2 && subItemParts.length >= 2) {
        const currentParent = currentParts[0]; // e.g., "settings"
        const subItemParent = subItemParts[0]; // e.g., "settings"
        
        if (currentParent === subItemParent) {
          // Extract the key word from each path (e.g., "user" from "userlist" or "adduser")
          const currentKey = currentParts[1]?.toLowerCase().replace(/^(add|edit)/, '').replace(/list$/, '') || '';
          const subItemKey = subItemParts[1]?.toLowerCase().replace(/list$/, '') || '';
          
          // If keys match (e.g., "user" matches "user"), this is likely the right sub-item
          if (currentKey && subItemKey && currentKey === subItemKey) {
            // Prefer exact parent path matches
            const score = subItemPath.length;
            if (score > bestMatchScore) {
              bestMatch = subItem.title;
              bestMatchScore = score;
            }
          }
        }
      }
    }
    
    return bestMatch;
  };

  // Helper function to find which menu item matches the current path
  const findActiveMenu = (pathname) => {
    // Normalize pathname (remove trailing slash if present, except root)
    const normalizedPath = pathname.endsWith('/') && pathname.length > 1 
      ? pathname.slice(0, -1) 
      : pathname;
    
    const currentPath = normalizePathForComparison(normalizedPath);
    
    // Check each menu item to see if current path matches any of its sub-items
    for (const menuItem of MenuList) {
      if (menuItem.content && menuItem.content.length > 0) {
        // Extract base path prefixes from all sub-items in this menu
        const pathPrefixes = new Set();
        for (const subItem of menuItem.content) {
          if (subItem.to) {
            const basePrefix = getBasePathPrefix(subItem.to);
            if (basePrefix) {
              pathPrefixes.add(basePrefix);
            }
          }
        }
        
        // Check if current path starts with any of the menu's path prefixes
        let matchedPrefix = null;
        for (const prefix of pathPrefixes) {
          if (currentPath.startsWith(prefix + '/') || currentPath === prefix) {
            matchedPrefix = prefix;
            break;
          }
        }
        
        if (matchedPrefix) {
          // Find the most relevant sub-item to highlight
          const bestMatch = findBestSubItemMatch(normalizedPath, menuItem);
          
          // If no specific match found, use appropriate default based on menu
          let defaultSubItem = menuItem.content[0]?.title || '';
          
          // For Documents menu, prefer "My Documents" over "Dashboard" as default
          if (!bestMatch && menuItem.title === 'Documents') {
            const myDocumentsItem = menuItem.content.find(item => item.title === 'My Documents');
            if (myDocumentsItem) {
              defaultSubItem = myDocumentsItem.title;
            }
          }

          if (!bestMatch && menuItem.title === 'Credentials Vault') {
            const navSide = getCredentialVaultNavSideFromPath(
              normalizedPath,
              location.search,
              location.state
            );
            if (navSide === 'personal') {
              const hit = menuItem.content.find((i) =>
                (i.to || '').includes('personalVaultList')
              );
              if (hit) defaultSubItem = hit.title;
            } else if (navSide === 'corporate') {
              const hit = menuItem.content.find((i) =>
                (i.to || '').includes('credentialVaultList')
              );
              if (hit) defaultSubItem = hit.title;
            } else {
              defaultSubItem = '';
            }
          }
          
          return { 
            menuTitle: menuItem.title, 
            submenuTitle: bestMatch || defaultSubItem
          };
        }
        
        // Check direct sub-items (exact matches)
        for (const subItem of menuItem.content) {
          const subItemPath = normalizePathForComparison(subItem.to);
          
          // Exact match or path starts with subItem path (for nested routes)
          if (subItem.to && (
            currentPath === subItemPath || 
            currentPath.startsWith(subItemPath + '/')
          )) {
            return { menuTitle: menuItem.title, submenuTitle: subItem.title };
          }
          
          // Check nested sub-items if they exist
          if (subItem.content && subItem.content.length > 0) {
            for (const nestedItem of subItem.content) {
              const nestedPath = normalizePathForComparison(nestedItem.to);
              
              if (nestedItem.to && (
                currentPath === nestedPath || 
                currentPath.startsWith(nestedPath + '/')
              )) {
                return { menuTitle: menuItem.title, submenuTitle: subItem.title };
              }
            }
          }
        }
      }
    }
    return null;
  };

  // Helper function to check if a path matches a menu item's route
  const isPathActive = (menuPath) => {
    if (!menuPath) return false;
    const normalizedMenuPath = menuPath.endsWith('/') && menuPath.length > 1 
      ? menuPath.slice(0, -1) 
      : menuPath;
    const normalizedCurrentPath = currentPath.endsWith('/') && currentPath.length > 1
      ? currentPath.slice(0, -1)
      : currentPath;
    
    // Normalize paths for comparison (add leading slash if needed)
    const menuPathToCheck = normalizedMenuPath.startsWith('/') 
      ? normalizedMenuPath 
      : '/' + normalizedMenuPath;
    
    // Exact match or current path starts with menu path (for nested routes)
    if (normalizedCurrentPath === menuPathToCheck || 
        normalizedCurrentPath.startsWith(menuPathToCheck + '/')) {
      return true;
    }
    
    // Special handling for Staff and Vehicle sections
    // Extract base path segment (e.g., "staff", "vehicle")
    const menuPathParts = menuPathToCheck.split('/').filter(p => p);
    const currentPathParts = normalizedCurrentPath.split('/').filter(p => p);
    
    // For Staff: if menu is 'staff/staffList' and current path starts with '/staff/', highlight it
    if (menuPathParts.length >= 1 && menuPathParts[0] === 'staff') {
      if (currentPathParts.length >= 1 && currentPathParts[0] === 'staff') {
        return true;
      }
    }
    
    // For Vehicle: if menu is '/vehicle/vehicleList' or 'vehicle/vehicleList' and current path starts with '/vehicle/', highlight it
    if ((menuPathParts.length >= 1 && menuPathParts[0] === 'vehicle') ||
        (menuPathToCheck.includes('vehicle'))) {
      if (currentPathParts.length >= 1 && currentPathParts[0] === 'vehicle') {
        return true;
      }
    }

    // Credentials Vault: two sub-items share the same first segment — match explicitly
    const menuIsVault =
      menuPathParts.length >= 1 && menuPathParts[0] === 'credential-vault';
    if (menuIsVault && menuPathToCheck.includes('credential-vault')) {
      if (currentPathParts.length < 1 || currentPathParts[0] !== 'credential-vault') {
        return false;
      }
      const onVaultSettings = currentPathParts[1] === 'vaultSettings';
      const subItemIsVaultSettings =
        menuPathParts[1] === 'vaultSettings' || menuPathToCheck.includes('/vaultSettings');
      if (subItemIsVaultSettings) {
        return onVaultSettings;
      }

      const navSide = getCredentialVaultNavSideFromPath(
        normalizedCurrentPath,
        location.search,
        location.state
      );
      const listSeg = menuPathParts[1];
      if (listSeg === 'credentialVaultList') return navSide === 'corporate';
      if (listSeg === 'personalVaultList') return navSide === 'personal';
      return false;
    }

    return false;
  };

  const handleMenuActive = (status) => {
    if (state.active === status) {
      // Don't close menu if we're currently on a page that belongs to this menu
      const activeMenu = findActiveMenu(location.pathname);
      if (activeMenu && activeMenu.menuTitle === status) {
        // Keep menu open if we're on a related page
        return;
      }
      setState({ active: "" });
    } else {
      setState({ active: status, activeSubmenu: "" });
    }
  };
  const handleSubmenuActive = (status) => {
    if (state.activeSubmenu === status) {
      setState({ activeSubmenu: "" });
    } else {
      setState({ activeSubmenu: status });
    }
  };

  // Handle menu item click - close sidebar on mobile
  const handleMenuItemClick = () => {
    if (isMobile && isOpen) {
      closeSidebar();
    }
  };
  
  // Helper to (re)check feature access permissions
  const recheckFeatureAccess = async () => {
    try {
      const [projectAccess, branchAccess, departmentAccess, worksheetAccess] = await Promise.all([
        hasFeatureAccess('project'),
        hasFeatureAccess('branch'),
        hasFeatureAccess('department'),
        hasFeatureAccess('worksheet')
      ]);
      setHasProjectAccess(projectAccess);
      setHasBranchAccess(branchAccess);
      setHasDepartmentAccess(departmentAccess);
      setHasWorksheetAccess(worksheetAccess);
    } catch (error) {
      console.error('Error checking feature access:', error);
      setHasProjectAccess(false);
      setHasBranchAccess(false);
      setHasDepartmentAccess(false);
      setHasWorksheetAccess(false);
    }
  };

  // Check document subscription access on mount and location change
  useEffect(() => {
    const checkDocumentAccess = async () => {
      try {
        // Clear cache first to ensure fresh data
        const { clearDocumentSubscriptionCache } = await import('../../../utils/documentAccess');
        clearDocumentSubscriptionCache();
        const hasAccess = await hasDocumentSubscription();
        setHasDocumentAccess(hasAccess);
      } catch (error) {
        console.error('Error checking document subscription:', error);
        setHasDocumentAccess(false);
      }
    };
    checkDocumentAccess();
  }, [location.pathname]); // Recheck when location changes (e.g., after login redirect)

  // Listen for user changes (login/logout) to refresh document access
  useEffect(() => {
    const handleUserChange = async () => {
      try {
        // Clear cache when user changes
        const { clearDocumentSubscriptionCache } = await import('../../../utils/documentAccess');
        clearDocumentSubscriptionCache();
        const hasAccess = await hasDocumentSubscription();
        setHasDocumentAccess(hasAccess);
      } catch (error) {
        console.error('Error refreshing document subscription on user change:', error);
        setHasDocumentAccess(false);
      }
    };

    // Listen for logout events
    const handleLogout = () => {
      setHasDocumentAccess(false);
    };

    // Listen for user storage changes (when user logs in/out)
    const handleStorageChange = (e) => {
      if (e.key === 'user' || e.key === 'logout') {
        if (e.key === 'logout') {
          handleLogout();
        } else if (e.key === 'user' && e.newValue) {
          // User logged in, check access
          handleUserChange();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('user-subscription-updated', handleUserChange);
    window.addEventListener('user-logged-in', handleUserChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('user-subscription-updated', handleUserChange);
      window.removeEventListener('user-logged-in', handleUserChange);
    };
  }, []);

  // Check feature access on component mount
  useEffect(() => {
    recheckFeatureAccess();
  }, [])

  // Listen for subscription updates to refresh features and access immediately
  useEffect(() => {
    const onSubscriptionUpdated = async () => {
      loadUserPlanFeatures();
      recheckFeatureAccess();
      // Also refresh document access
      try {
        const hasAccess = await hasDocumentSubscription();
        setHasDocumentAccess(hasAccess);
      } catch (error) {
        console.error('Error refreshing document subscription:', error);
        setHasDocumentAccess(false);
      }
    };
    window.addEventListener('user-subscription-updated', onSubscriptionUpdated);
    return () => window.removeEventListener('user-subscription-updated', onSubscriptionUpdated);
  }, [])

  // Track previous path for navigation detection
  const prevPathRef = useRef(location.pathname);
  const lastMenuStateRef = useRef({ path: location.pathname, active: '', activeSubmenu: '' });
  
  // Auto-expand menus on page navigation and ensure they stay open
  // This now works dynamically for all menus, not just hardcoded ones
  useEffect(() => {
    const isNavigation = prevPathRef.current !== location.pathname;
    prevPathRef.current = location.pathname;
    
    // Skip if user just manually clicked (give them control)
    if (userManualClick) return;
    
    const activeMenu = findActiveMenu(location.pathname);
    if (activeMenu) {
      // Check if we need to update the state
      const needsUpdate = 
        lastMenuStateRef.current.path !== location.pathname ||
        lastMenuStateRef.current.active !== activeMenu.menuTitle ||
        lastMenuStateRef.current.activeSubmenu !== (activeMenu.submenuTitle || '');
      
      if (needsUpdate) {
        setState({ active: activeMenu.menuTitle, activeSubmenu: activeMenu.submenuTitle || '' });
        lastMenuStateRef.current = {
          path: location.pathname,
          active: activeMenu.menuTitle,
          activeSubmenu: activeMenu.submenuTitle || ''
        };
      }
    } else {
      // If no active menu found, clear the ref (but don't close menus that might be manually opened)
      lastMenuStateRef.current.path = location.pathname;
    }
  }, [location.pathname, userManualClick])
  
  // Reset manual click flag after click
  useEffect(() => {
    if (userManualClick) {
      const resetTimer = setTimeout(() => setUserManualClick(false), 200);
      return () => clearTimeout(resetTimer);
    }
  }, [userManualClick])

  return (
    <div
      className={`dlabnav ${iconHover} ${sidebarposition.value === "fixed" &&
        sidebarLayout.value === "horizontal" &&
        headerposition.value === "static"
        ? hideOnScroll > 120
          ? "fixed"
          : ""
        : ""
        } ${isOpen ? 'sidebar-open' : 'sidebar-closed'}`}
    >
      <PerfectScrollbar className="dlabnav-scroll">
        <ul className="metismenu" id="menu">
          {MenuList.map((data, index) => {
            let menuClass = data.classsChange;

            // Access Control: hide parent if no children would be visible
            const getVisibleContent = () => {
              if (!data.content) return [];
              const checkAccess = (subItem) => {
                if (subItem.publicAccess) return true;
                if (menuAccessKeys !== null && Array.isArray(menuAccessKeys)) {
                  return submenuAllowedByMyAccess(menuAccessKeys, menuAccessRoutePaths, subItem);
                }
                if (menuAccessFetchResolved && menuAccessKeys === null && subItem.menu_key) return false;
                if (!subItem.role) return true;
                const role = normalizeRole(subItem.role);
                const currentRole = normalizeRole(userRole || '');
                if (subItem.restricted_roles?.some(r => currentRole === normalizeRole(r))) return false;
                if (role === 'corporate_admin') return isCorporateAdmin(userRole);
                if (role === 'admin') return hasAdminRole(userRole);
                return currentRole === role;
              };
              return data.content.filter(checkAccess);
            };
            const visibleContent = getVisibleContent();
            if (data.content?.length > 0 && visibleContent.length === 0) return null;

            // Check if Documents menu should be locked
            const isDocumentsMenuLocked = data.title === 'Documents' && !hasDocumentAccess;
            
            if (menuClass === "menu-title") {
              return (
                <li className={menuClass} key={index}>{data.title}</li>
              )
            } else {
              const hasSubmenu = data.content && data.content.length > 0;
              const singleSubmenu =
                hasSubmenu &&
                data.content.length === 1 &&
                !data.alwaysShowSubmenu;

              return (
                <li
                  className={state.active === data.title ? "mm-active" : ""}
                  key={index}
                >
                  {/* If only one submenu → direct link */}
                  {singleSubmenu ? (
                    isDocumentsMenuLocked ? (
                      <Link
                        to={`${subscriptionFeatureRequiredPath}?module=document`}
                        onClick={handleMenuItemClick}
                        title="Document module is not available in your current subscription plan."
                      >
                        {data.iconStyle}
                        <span className="nav-text">{data.title}</span>
                        <i className="fas fa-lock" style={{ marginLeft: 6, fontSize: 12 }}></i>
                      </Link>
                    ) : (
                      <Link to={data.content[0].to} onClick={() => {
                        handleMenuActive(data.title);
                        handleMenuItemClick();
                      }}>
                        {data.iconStyle}
                        <span className="nav-text">{data.title}</span>
                      </Link>
                    )
                  ) : hasSubmenu ? (
                    <>
                      {isDocumentsMenuLocked ? (
                        <Link
                          to={`${subscriptionFeatureRequiredPath}?module=document`}
                          className="has-arrow"
                          onClick={handleMenuItemClick}
                          title="Document module is not available in your current subscription plan."
                        >
                          {data.iconStyle}
                          <span className="nav-text">{data.title}</span>
                          <i className="fas fa-lock" style={{ marginLeft: 6, fontSize: 12 }}></i>
                        </Link>
                      ) : (
                        <Link
                          to={"#"}
                          className="has-arrow"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            //Force update state for fixing dropdown toggle
                            setUserManualClick(true);
                            setState({ active: data.title, activeSubmenu: "" });
                          }}
                        >
                          {data.iconStyle}
                          <span className="nav-text">{data.title}</span>
                        </Link>
                      )}
                      <Collapse in={state.active === data.title && !isDocumentsMenuLocked}>
                        <ul className="mm-show mm-collapse">
                          {visibleContent.map((subItem, subIndex) => {
                            // Lock all sub-items if Documents menu is locked
                            const isSubItemLocked = isDocumentsMenuLocked;
                            // Helper function to check if user can access this menu item
                            const canAccessMenuItem = () => {
                              if (subItem.publicAccess) return true;
                              // Access Control: if we have menuKeys from API, use them for visibility
                              if (menuAccessKeys !== null && Array.isArray(menuAccessKeys)) {
                                return submenuAllowedByMyAccess(menuAccessKeys, menuAccessRoutePaths, subItem);
                              }

                              // Fallback: role-based access (when API not yet loaded or unavailable)
                              if (menuAccessFetchResolved && menuAccessKeys === null && subItem.menu_key) return false;
                              if (!subItem.role) return true;

                              const role = normalizeRole(subItem.role);
                              const currentRole = normalizeRole(userRole || '');

                              if (subItem.restricted_roles && Array.isArray(subItem.restricted_roles)) {
                                const isRestricted = subItem.restricted_roles.some(restrictedRole =>
                                  currentRole === normalizeRole(restrictedRole)
                                );
                                if (isRestricted) return false;
                              }

                              if (role === 'corporate_admin') return isCorporateAdmin(userRole);
                              if (role === 'admin') return hasAdminRole(userRole);
                              return currentRole === role;
                            };

                            if (!canAccessMenuItem()) return null;

                            // Role-based checks (only when Access Control API not used)
                            if (menuAccessKeys === null) {
                              if (subItem.to === 'settings/userlist' && !canAccessUsers(userRole)) return null;
                              if (subItem.to === 'staff/staffList' && !canAccessStaff(userRole)) return null;
                              if (subItem.to === 'settings/branchList' && !canAccessBranches(userRole)) return null;
                              if (subItem.to === '/vehicle/vehicleList' && !canAccessVehicles(userRole)) return null;
                              if (subItem.to === 'settings/designationList' && !canAccessDesignations(userRole)) return null;
                              if (subItem.to === '/task/worksheet' && !canAccessWorksheet(userRole)) return null;
                              if (subItem.to === 'settings/departmentList' && !canAccessDepartments(userRole)) return null;
                              if (subItem.to === 'settings/subDepartmentList' && !canAccessSubDepartments(userRole)) return null;
                              if (subItem.to === 'settings/userSettings' && !canAccessUserSettings(userRole)) return null;
                            }
                            
                            // Check if feature_key exists and if user has access
                            let isLocked = false;
                            if (subItem.publicAccess) {
                              isLocked = false;
                            } else if (isSubItemLocked) {
                              isLocked = true;
                            } else if (subItem.feature_key === 'project') {
                              isLocked = !hasProjectAccess;
                            } else if (subItem.feature_key === 'branch') {
                              isLocked = !hasBranchAccess;
                            } else if (subItem.feature_key === 'department') {
                              isLocked = !hasDepartmentAccess;
                            } else if (subItem.feature_key === 'worksheet') {
                              isLocked = !hasWorksheetAccess;
                            } else if (subItem.feature_key) {
                              isLocked = !userPlanFeaturesState.includes(subItem.feature_key);
                            }
                            return (
                              <li
                                key={subIndex}
                                className={state.activeSubmenu === subItem.title ? "mm-active" : ""}
                              >
                                {subItem.content && subItem.content.length > 0 ? (
                                  <>
                                    {isLocked ? (
                                      <Link
                                        to={`${subscriptionFeatureRequiredPath}?module=${(subItem.feature_key === 'document' || isDocumentsMenuLocked) ? 'document' : 'task'}`}
                                        className={subItem.hasMenu ? "has-arrow" : ""}
                                        onClick={handleMenuItemClick}
                                        title="This feature is not available in your current plan."
                                      >
                                        {subItem.title} <i className="fas fa-lock" style={{ marginLeft: 6 }}></i>
                                      </Link>
                                    ) : (
                                      <Link
                                        to={subItem.to}
                                        className={subItem.hasMenu ? "has-arrow" : ""}
                                        onClick={() => handleSubmenuActive(subItem.title)}
                                      >
                                        {subItem.title}
                                      </Link>
                                    )}
                                    <Collapse in={state.activeSubmenu === subItem.title}>
                                      <ul
                                        className={`${menuClass === "mm-collapse" ? "mm-show" : ""}`}
                                      >
                                        {subItem.content.map((nestedItem, nestedIndex) => (
                                          <li key={nestedIndex}>
                                            <Link
                                              to={nestedItem.to}
                                              className={`${isPathActive(nestedItem.to) ? "mm-active" : ""}`}
                                              onClick={handleMenuItemClick}
                                            >
                                              {nestedItem.title}
                                            </Link>
                                          </li>
                                        ))}
                                      </ul>
                                    </Collapse>
                                  </>
                                ) : (
                                  (() => {
                                    if (isLocked) {
                                      return (
                                        <Link
                                          to={`${subscriptionFeatureRequiredPath}?module=${(subItem.feature_key === 'document' || isDocumentsMenuLocked) ? 'document' : 'task'}`}
                                          className={`${isPathActive(subItem.to) ? "mm-active" : ""}`}
                                          onClick={handleMenuItemClick}
                                          title="This feature is not available in your current plan."
                                        >
                                          {subItem.title} <i className="fas fa-lock" style={{ marginLeft: 6, fontSize: 12 }}></i>
                                        </Link>
                                      );
                                    } else {
                                      return (
                                        <Link
                                          to={subItem.hasMenu ? "#" : subItem.to}
                                          className={`${isPathActive(subItem.to) || state.activeSubmenu === subItem.title ? "mm-active" : ""}`}
                                          onClick={subItem.hasMenu ? undefined : handleMenuItemClick}
                                        >
                                          {subItem.title}
                                        </Link>
                                      );
                                    }
                                  })()
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </Collapse>
                    </>
                  ) : (
                    <Link to={data.to} onClick={handleMenuItemClick}>
                      {data.iconStyle}
                      <span className="nav-text">{data.title}</span>
                    </Link>
                  )}
                </li>
              );
            }
          })}
        </ul>
      </PerfectScrollbar>
    </div>
  );
};

export default SideBar;
